import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { applyPdfPermissions } from './permissionsPdfEngine';
import { zipPdfFiles } from '../split/splitPdfEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

const PERMISSION_OPTIONS = [
    { id: 'allowPrint', labelKey: 'permissionsPdf.allowPrint' },
    { id: 'allowCopy', labelKey: 'permissionsPdf.allowCopy' },
    { id: 'allowModify', labelKey: 'permissionsPdf.allowModify' },
    { id: 'allowAnnotate', labelKey: 'permissionsPdf.allowAnnotate' },
    { id: 'allowForms', labelKey: 'permissionsPdf.allowForms' },
];

export default function PermissionsPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [perms, setPerms] = useState({
        allowPrint: true,
        allowCopy: false,
        allowModify: false,
        allowAnnotate: false,
        allowForms: true,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        let hadError = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const f of list) {
                if (!(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('permissionsPdf.errors.invalidType'), color: 'red' });
                    continue;
                }
                if (f.size > MAX_FILE_BYTES) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('permissionsPdf.errors.tooLarge'), color: 'red' });
                    continue;
                }
                next.push(f);
            }
            return next;
        });
        if (hadError) setError(t('permissionsPdf.errors.failed'));
        else setError(null);
        setResults([]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResults([]);
        setError(null);
    };

    const clearResultsOnly = () => {
        setResults([]);
        setError(null);
    };

    const clearAll = () => {
        setFiles([]);
        setPassword('');
        setConfirmPassword('');
        setResults([]);
        setError(null);
        setPreviewIndex(0);
    };

    const handleDownloadZip = async () => {
        if (!results.length) return;
        const zip = await zipPdfFiles(results.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'restricted-pdfs.zip');
    };

    const togglePerm = (id) => {
        setPerms((prev) => ({ ...prev, [id]: !prev[id] }));
        setResults([]);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!files.length || processing) return;
        if (!password) {
            const msg = t('permissionsPdf.errors.emptyPassword');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (password !== confirmPassword) {
            const msg = t('permissionsPdf.errors.mismatch');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('pdf/permissions', { category: 'pdf' });

        setProcessing(true);
        setResults([]);
        setError(null);

        const done = [];
        // Names of the PDFs that failed, by reason: one bad PDF must not hide the others'
        // results, so these are only a warning when something did succeed.
        const failed = { alreadyEncrypted: [], other: [] };

        for (const file of files) {
            try {
                const blob = await applyPdfPermissions(file, password, perms);
                done.push({
                    name: `${file.name.replace(/\.pdf$/i, '')}-restricted.pdf`,
                    originalSize: file.size,
                    restrictedSize: blob.size,
                    blob,
                });
            } catch (err) {
                if (err?.message === 'alreadyEncrypted') failed.alreadyEncrypted.push(file.name);
                else failed.other.push(file.name);
            }
        }

        setResults(done);
        setProcessing(false);

        const problems = [
            [failed.alreadyEncrypted, 'permissionsPdf.errors.alreadyEncrypted'],
            [failed.other, 'permissionsPdf.errors.failed'],
        ].filter(([names]) => names.length);
        const color = done.length ? 'yellow' : 'red';
        const title = done.length ? t('common.warning') : t('common.error');
        for (const [names, key] of problems) {
            const msg = `${t(key)}${files.length > 1 ? ` (${names.join(', ')})` : ''}`;
            notifications.show({ title, message: msg, color });
        }
        if (done.length) {
            notifications.show({ title: t('common.success'), message: t('permissionsPdf.done'), color: 'green' });
        }
        // The panel shows the error only when there is nothing else to show.
        setError(!done.length && problems.length ? t(problems[0][1]) : null);
    };

    const canSubmit = files.length > 0 && !processing;

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: !!canSubmit,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        success: t('permissionsPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const activeResult = results[previewIndex] ?? results[0] ?? null;
    const successLabel = results.length > 1
        ? `${t('permissionsPdf.resultTitle')} (${toBnDigits(results.length)})`
        : activeResult
            ? formatFileSize(activeResult.blob.size)
            : '';

    const resultDownloads = useMemo(() => {
        if (!results.length) return undefined;
        return results.map((r) => ({
            id: r.name,
            name: r.name,
            originalSize: formatFileSize(r.originalSize),
            compressedSize: formatFileSize(r.restrictedSize),
            onDownload: () => downloadBlob(r.blob, r.name),
        }));
    }, [results]);

    return (
        <ToolPageLayout
            slug="pdf/permissions"
            subtitleKey="permissionsPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || results.length > 0 || error ? clearAll : undefined}
            primaryAction={{
                label: t('permissionsPdf.apply'),
                onClick: handleSubmit,
                disabled: !canSubmit || results.length > 0,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('permissionsPdf.dropTitle')}
                            hint={t('permissionsPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : null}

                    {files.length > 0 && (
                        <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                            {files.map((file, index) => (
                                <PdfUploadedFile
                                    key={`${file.name}-${index}`}
                                    name={file.name}
                                    meta={formatFileSize(file.size)}
                                    selected={previewIndex === index}
                                    onSelect={() => setPreviewIndex(index)}
                                    onRemove={() => removeFile(index)}
                                    removeLabel={t('permissionsPdf.removeFile')}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
            settings={(
                <>
                    {files.length > 0 && (
                        <>
                            <ToolCard title={t('permissionsPdf.permissionsTitle')}>
                                <div className="space-y-2">
                                    {PERMISSION_OPTIONS.map(({ id, labelKey }) => (
                                        <label key={id} className="flex cursor-pointer items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                                            <input
                                                type="checkbox"
                                                checked={perms[id]}
                                                onChange={() => togglePerm(id)}
                                                className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary/30 dark:border-zinc-600"
                                            />
                                            {t(labelKey)}
                                        </label>
                                    ))}
                                    <p className="pt-1 text-xs text-zinc-400 dark:text-zinc-500">{t('permissionsPdf.permissionsHint')}</p>
                                </div>
                            </ToolCard>

                            <ToolCard title={t('permissionsPdf.passwordLabel')}>
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <label htmlFor="perm-password" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('permissionsPdf.passwordLabel')}
                                        </label>
                                        <input
                                            id="perm-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => { setPassword(e.target.value); setError(null); setResults([]); }}
                                            placeholder={t('permissionsPdf.passwordPlaceholder')}
                                            className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                        />
                                        <p className="text-xs text-zinc-400 dark:text-zinc-500">{t('permissionsPdf.passwordHint')}</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label htmlFor="perm-confirm" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('permissionsPdf.confirmLabel')}
                                        </label>
                                        <input
                                            id="perm-confirm"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => { setConfirmPassword(e.target.value); setError(null); setResults([]); }}
                                            placeholder={t('permissionsPdf.confirmPlaceholder')}
                                            className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                        />
                                    </div>
                                </div>
                            </ToolCard>
                        </>
                    )}
                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={t('permissionsPdf.processing')}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('permissionsPdf.download')}
                    downloads={resultDownloads}
                    downloadVariant="compress"
                    bundleDownload={results.length > 1
                        ? { label: t('permissionsPdf.downloadZip'), onClick: handleDownloadZip }
                        : undefined}
                    onEditAgain={results.length > 0 ? clearResultsOnly : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={results.length > 0 ? clearAll : undefined}
                    againLabel={t('permissionsPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('permissionsPdf.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={(files.length > 0 || activeResult) && !processing}
                >
                    {(files.length > 0 || activeResult) && !processing ? (
                        <PdfDocPreview
                            files={files}
                            resultBlobs={results.length ? results.map((r) => r.blob) : undefined}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
